<template>
  <div class="bg-white rounded-2xl shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300 p-8 w-full mb-6">
    <div class="flex justify-between items-center mb-8">
      <div>
        <h2 class="text-2xl font-medium text-zinc-800">
          {{ __(`Hello {name}! Let's get this challenge started. Are you ready?`, { name }) }}
        </h2>
        <p class="text-base text-zinc-700 mt-1">
          {{ __(`Completing this challenge will give you a prime opportunity to highlight your skills.`) }}
        </p>
      </div>
    </div>

    <div class="rounded-2xl bg-[#F2E7FF] p-6 mb-8">
      <div class="flex justify-between items-center">
        <h3 class="text-lg font-semibold text-zinc-800 mb-3">
          {{ __('How should you navigate the challenge?') }}
        </h3>
        <div class="text-base text-zinc-600">
          <span class="font-semibold">{{ __('Duration: ') }}</span>
          <span>{{ formattedDuration }}</span>
        </div>
      </div>
      <InstructionsList :instructions="challenge.instructions" />
    </div>

    <div class="flex justify-between">
      <button
          type="button"
          @click="redirectToIndex"
          class="inline-flex items-center px-6 py-2 hover-lift-no-shadow text-gray-700 hover:bg-gray-50 hover:rounded-md transition-colors"
          variant="ghost-no-padding"
      >
        <Icon name="arrow_back_ios" class="w-5 h-5  text-xl text-zinc-800" />
        {{ __('Back To Challenges') }}
      </button>
      <div class="ml-auto flex gap-3">
        <button
            type="button"
            @click="handleToggleSave"
            :class="[
              'px-5 py-2 rounded-md border-2 transition-colors flex items-center gap-2',
              isSaved
                ? 'bg-green-500 text-white hover:bg-green-600 border-green-500'
                : 'bg-none text-primary hover:bg-primary/10 border-primary'
            ]"
        >
          <Icon
              :name="isSaved ? 'playlist_add_check' : 'playlist_add'"
              class="text-2xl"
              :class="isSaved ? 'text-white' : 'text-primary'"
          />
          <span class="text-base">{{ saving ? __('Saving...') : (isSaved ? __('Saved') : __('Save For Later')) }}</span>
        </button>
        <button
            type="button"
            @click="handleStartChallenge()"
            class="px-5 py-2 rounded-md transition-colors bg-primary text-white hover:bg-primary/90"
        >
          <span class="text-base">{{ __('Continue') }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import {computed, ref} from 'vue';
import InstructionsList from '@/pages/candidate/challenges/components/InstructionsList.vue';
import {Icon} from "@/components/block/icon";
import {__} from "@/helpers";
import axios from "axios";
import {useToast} from "vue-toastification";

const props = defineProps({
  challenge: Object,
  name: String,
});

const saving = ref(false);
const toast = useToast();
const isSaved = ref(props.challenge.is_saved || false);

const formattedDuration = computed(() => {
  const total = props.challenge.time_limit_minutes || 0;

  if (total < 60) {
    const unit = total === 1
        ? __('minute')
        : __('minutes')
    return `${total} ${unit}`
  }

  const h = Math.floor(total / 60);
  const m = total % 60;

  return m
      ? `${h}h ${m}m`
      : `${h}h`;
});

function redirectToIndex() {
  window.location.href = route('challenges.index');
}

const handleToggleSave = async () => {
  if (isSaved.value) {
    await handleUnsave();
  } else {
    await handleSave();
  }
};

const handleSave = async () => {
  saving.value = true;
  try {
    const response = await axios.post(route('redesign.candidate.challenges.save', {
      challenge: props.challenge.id
    }));

    toast.success(response.data.message || __('Challenge saved successfully'));
    isSaved.value = true;
  } catch(error) {
    toast.error(
        error.response?.data?.message || __('Error saving the challenge')
    );
  } finally {
    saving.value = false;
  }
};

const handleUnsave = async () => {
  saving.value = true;
  try {
    const response = await axios.delete(route('redesign.candidate.challenges.unsave', {
      challenge: props.challenge.id
    }));

    toast.success(response.data.message || __('Challenge removed from saved list'));
    isSaved.value = false;
  } catch(error) {
    toast.error(
        error.response?.data?.message || __('Error removing saved challenge')
    );
  } finally {
    saving.value = false;
  }
};

const handleStartChallenge = async () => {
  if (!props.challenge.id) {
    sessionStorage.setItem('pending_challenge_start', props.challenge.id);
    window.location.href = `/login?redirect=${encodeURIComponent(window.location.href)}&action=start`;
    return;
  }
  try {
    const response = await axios.post(route('redesign.candidate.challenges.start', {
      challenge: props.challenge.id
    }));

    if (response.data.redirect) {
      window.location.href = response.data.redirect;
    }
  } catch (error) {
    toast.error(error.response?.data?.message || __('Failed to start challenge'));
  }
};
</script>
